import React, { useState, useContext } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { DestinationContext } from '../../../contexts/DestinationContext';
import styles from './FinalCta.module.scss';

const routeApi = getRouteApi('/');

const defaultCopy = (destinationName) => ({
  eyebrow: 'Let the Journey Begin',
  title: 'The Wilderness is Calling',
  subtext: `We connect you directly to ${destinationName ? `${destinationName}'s` : "Kenya's"} most trusted, vetted safari operators — and let them compete to design your perfect trip. Fill in the form and receive three fully tailored proposals, each competing for your business.`,
  trustBullets: [
    'Free & no obligation',
    'Reply within 24 hours',
    'No commission, no mark-up',
  ],
  formHeading: 'Design your Safari ',
  stepLabel: 'Step 1 of 2 — Your Trip',
});

const FinalCta = ({ onOpenPlanner }) => {
  const destination = useContext(DestinationContext);
  const { finalCtaSubtext } = routeApi.useLoaderData();
  const [arrival, setArrival] = useState('');
  const [nights, setNights] = useState('5');
  const [guests, setGuests] = useState('');
  const [budget, setBudget] = useState('');

  const submit = (e) => {
    e.preventDefault();
    // Budget is captured as a representative price point so the shared
    // planner can derive its mid-range/luxury classification (>$650) from it.
    const pricePerPerson =
      budget === '650+' ? 800 : budget === '0-650' ? 400 : undefined;
    onOpenPlanner?.({
      startDate: arrival,
      numberOfDays: nights,
      adults: guests ? Number(guests) : undefined,
      pricePerPerson,
    });
  };

  // Merge field-by-field: `destination.finalCta` may be present but only
  // partially filled in by an admin (fields can be `{}`, missing, or present
  // as empty strings), so neither a whole-object `||` nor a blind spread is
  // enough — fall back per field whenever the admin-provided value is falsy.
  const defaults = defaultCopy(destination?.name);
  const provided = destination?.finalCta || {};
  const finalCta = Object.fromEntries(
    Object.keys(defaults).map((key) => [
      key,
      (
        Array.isArray(provided[key])
          ? provided[key].length > 0
          : !!provided[key]
      )
        ? provided[key]
        : defaults[key],
    ])
  );

  // `finalCta.subtext` is rich HTML when it comes from the destination
  // record (sanitized server-side in the loader); the hardcoded fallback
  // copy is plain text but safe to render the same way (no markup in it).
  const subtext = provided.subtext
    ? (finalCtaSubtext ?? provided.subtext)
    : defaults.subtext;

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div>
          <div className={styles.eyebrow}>{finalCta.eyebrow}</div>
          <h2
            className={styles.heading}
            dangerouslySetInnerHTML={{ __html: finalCta.title }}
          />
          <div
            className={styles.body}
            dangerouslySetInnerHTML={{ __html: subtext }}
          />
          <div className={styles.ticks}>
            {Array.isArray(finalCta.trustBullets) &&
              finalCta.trustBullets.map((bullet) => (
                <span key={bullet}>✓ {bullet}</span>
              ))}
          </div>
        </div>
        <form className={styles.card} onSubmit={submit}>
          <h3>{finalCta.formHeading}</h3>
          <div className={styles.step}>{finalCta.stepLabel}</div>
          <div className={styles.g2}>
            <div>
              <label>Arrival date</label>
              <input
                type="date"
                value={arrival}
                onChange={(e) => setArrival(e.target.value)}
              />
            </div>
            <div>
              <label>Number of nights</label>
              <input
                value={nights}
                onChange={(e) => setNights(e.target.value)}
              />
            </div>
            <div>
              <label>Number of guests</label>
              <input
                type="number"
                min="1"
                placeholder="2"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
              />
            </div>
            <div>
              <label>Budget per person/night</label>
              <select
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              >
                <option value="">Prefer not to say</option>
                <option value="0-650">$0 – $650</option>
                <option value="650+">$650+</option>
              </select>
            </div>
          </div>
          <button type="submit">Continue →</button>
          <div className={styles.note}>
            Free service · No commission · Direct contact with operators
          </div>
        </form>
      </div>
    </section>
  );
};

export default FinalCta;
